import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './AddPayment.css';
import RTPageLayout from '../common/RTPageLayout';

const AddAdjustment = ({ onClose, invoiceData, pgData }) => {
  const location = useLocation();
  const [adjustmentDate, setAdjustmentDate] = useState(new Date());
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState(
    invoiceData?.amount_remaining != null
      ? parseFloat(invoiceData.amount_remaining).toFixed(2)
      : '0.00',
  );
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const getRemainingAfterAdjustment = () => {
    return (
      parseFloat(invoiceData?.amount_remaining || 0) - parseFloat(amount || 0)
    );
  };

  const validate = () => {
    const newErrors = {};

    if (!reason.trim()) {
      newErrors.reason = 'Reason for adjustment is required';
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else if (parsedAmount > parseFloat(invoiceData?.amount_remaining || 0)) {
      newErrors.amount = `Amount cannot exceed remaining amount (₹${parseFloat(invoiceData?.amount_remaining || 0).toFixed(2)})`;
    }

    if (!adjustmentDate) {
      newErrors.adjustmentDate = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setProcessing(true);

      const payload = {
        invoice_id: invoiceData.invoice_id,
        pg_id: pgData.pg_id,
        amount: parseFloat(amount),
        adjustment_date: adjustmentDate.toISOString().split('T')[0],
        reason: reason.trim(),
      };

      await apiPost('/payments/adjustment', payload);
      toastSuccess('Adjustment added successfully');
      onClose(true);
    } catch (error) {
      console.error('Error creating adjustment:', error);
      const errorMsg =
        error?.response?.data?.detail || 'Failed to create adjustment';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Add Adjustment"
      actions={
        <>
          <RTButton variant="secondary" onClick={handleCancel}>
            ← Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <div className="add-payment-section">
        {/* Left Section - Adjustment Form */}
        <div className="add-payment-form">
          <h2 className="rt-form-title">Adjustment Details</h2>

          {/* Invoice Info */}
          <div
            style={{
              backgroundColor: '#e8f0fe',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '18px',
              border: '1px solid #c6d9f1',
            }}
          >
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontWeight: 600, color: '#153C7F' }}>
                  Invoice:{' '}
                </span>
                <span>{invoiceData?.invoice_number || '-'}</span>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: '#153C7F' }}>PG: </span>
                <span>{pgData?.name || '-'}</span>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: '#153C7F' }}>
                  Total Amount:{' '}
                </span>
                <span>
                  ₹{parseFloat(invoiceData?.total_amount || 0).toFixed(2)}
                </span>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: '#153C7F' }}>
                  Amount Paid:{' '}
                </span>
                <span>
                  ₹{parseFloat(invoiceData?.amount_paid || 0).toFixed(2)}
                </span>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: '#153C7F' }}>
                  Amount Remaining:{' '}
                </span>
                <span style={{ color: '#dc3545', fontWeight: 600 }}>
                  ₹{parseFloat(invoiceData?.amount_remaining || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Adjustment Date */}
          <div className="add-payment-form-group">
            <label>
              Adjustment Date <span style={{ color: 'red' }}>*</span>
            </label>
            <DatePicker
              selected={adjustmentDate}
              onChange={(date) => {
                setAdjustmentDate(date);
                setErrors((prev) => ({ ...prev, adjustmentDate: '' }));
              }}
              dateFormat="dd/MM/yyyy"
              className={`add-payment-input ${errors.adjustmentDate ? 'is-invalid' : ''}`}
              maxDate={new Date()}
            />
            {errors.adjustmentDate && (
              <div
                className="text-danger"
                style={{ fontSize: '0.85rem', marginTop: '4px' }}
              >
                {errors.adjustmentDate}
              </div>
            )}
          </div>

          {/* Amount */}
          <div className="add-payment-form-group">
            <label>
              Adjustment Amount <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={invoiceData?.amount_remaining || 0}
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setErrors((prev) => ({ ...prev, amount: '' }));
              }}
              className={`add-payment-input ${errors.amount ? 'is-invalid' : ''}`}
              placeholder="Enter adjustment amount"
            />
            {errors.amount && (
              <div
                className="text-danger"
                style={{ fontSize: '0.85rem', marginTop: '4px' }}
              >
                {errors.amount}
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="add-payment-form-group">
            <label>
              Reason for Adjustment <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setErrors((prev) => ({ ...prev, reason: '' }));
              }}
              className={`add-payment-input ${errors.reason ? 'is-invalid' : ''}`}
              rows={3}
              placeholder="Enter reason for adjustment (e.g., Discount, Waiver, Correction)"
              style={{ resize: 'vertical' }}
            />
            {errors.reason && (
              <div
                className="text-danger"
                style={{ fontSize: '0.85rem', marginTop: '4px' }}
              >
                {errors.reason}
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Summary */}
        <div className="add-payment-summary">
          <h2 className="payment-summary-title">Adjustment Summary</h2>

          {/* Invoice Details */}
          <div className="add-payment-summary-section">
            <h3 className="add-payment-summary-subtitle">Invoice Details</h3>
            <div className="add-payment-summary-item">
              <span className="add-payment-summary-label">Invoice:</span>
              <span className="add-payment-summary-value">
                {invoiceData?.invoice_number || '-'}
              </span>
            </div>
            <div className="add-payment-summary-item">
              <span className="add-payment-summary-label">Status:</span>
              <span className="add-payment-summary-value">
                {invoiceData?.status?.charAt(0).toUpperCase() +
                  invoiceData?.status?.slice(1).replace('_', ' ') || '-'}
              </span>
            </div>
          </div>

          {/* Amount Details */}
          <div className="add-payment-summary-section">
            <h3 className="add-payment-summary-subtitle">Amount Details</h3>
            <div className="add-payment-summary-item">
              <span className="add-payment-summary-label">
                Current Remaining:
              </span>
              <span className="add-payment-summary-value">
                ₹{parseFloat(invoiceData?.amount_remaining || 0).toFixed(2)}
              </span>
            </div>
            <div className="add-payment-summary-item">
              <span className="add-payment-summary-label">
                Adjustment Amount:
              </span>
              <span
                className="add-payment-summary-value"
                style={{ color: '#28a745', fontWeight: 'bold' }}
              >
                ₹{parseFloat(amount || 0).toFixed(2)}
              </span>
            </div>
            <div className="add-payment-summary-item">
              <span className="add-payment-summary-label">
                Remaining After Adjustment:
              </span>
              <span
                className="add-payment-summary-value"
                style={{
                  color:
                    getRemainingAfterAdjustment() <= 0 ? '#28a745' : '#153C7F',
                  fontWeight: 'bold',
                }}
              >
                ₹{getRemainingAfterAdjustment().toFixed(2)}
              </span>
            </div>
            <div className="add-payment-summary-item">
              <span className="add-payment-summary-label">New Status:</span>
              <span
                className="add-payment-summary-value"
                style={{ fontWeight: 'bold' }}
              >
                {getRemainingAfterAdjustment() <= 0 ? 'Paid' : 'Partially Paid'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="add-payment-summary-actions">
            <RTButton
              variant="secondary"
              onClick={handleCancel}
              disabled={processing}
              className="add-payment-action-button add-payment-cancel-button"
            >
              Cancel
            </RTButton>
            <RTButton
              variant="primary"
              onClick={handleSave}
              disabled={processing || !reason.trim()}
              className="add-payment-action-button add-payment-submit-button"
            >
              {processing ? 'Processing...' : 'Save'}
            </RTButton>
          </div>
        </div>
      </div>

      {/* Processing Overlay */}
      {processing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <RTSpinner
            size="medium"
            showMessage={true}
            message="Processing adjustment..."
          />
        </div>
      )}
    </RTPageLayout>
  );
};

export default AddAdjustment;
